/**
 * Auth Service - Handles all authentication-related API operations
 * Following the same pattern as callsService with modern API client
 */

import { apiClient, ApiClientError, type ModernApiResponse } from './apiClient';

// Auth Types
export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  password: string;
  password_confirm: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// Custom error class for auth API
export class AuthApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

class AuthService {
  private readonly baseEndpoint = 'auth';

  /**
   * Request password reset
   */
  async requestPasswordReset(data: PasswordResetRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.postModern<any>(
        `${this.baseEndpoint}/password-reset/`,
        data
      );

      if (apiClient.isSuccessResponse(response)) {
        return {
          success: true,
          message: apiClient.getSuccessMessage(response) || 'Password reset email sent successfully',
          data: response.data
        };
      } else {
        throw new AuthApiError(
          apiClient.getErrorMessage(response),
          apiClient.getErrorCode(response),
          apiClient.getErrorDetails(response)
        );
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
      
      if (error instanceof AuthApiError) {
        throw error;
      }
      
      if (error instanceof ApiClientError) {
        throw new AuthApiError(
          error.message,
          error.code,
          error.details
        );
      }
      
      throw new AuthApiError(
        'Failed to send password reset email. Please try again.',
        'NETWORK_ERROR',
        error
      );
    }
  }

  /**
   * Confirm password reset
   */
  async confirmPasswordReset(data: PasswordResetConfirm): Promise<AuthResponse> {
    try {
      const response = await apiClient.postModern<any>(
        `${this.baseEndpoint}/password-reset-confirm/`,
        data
      );

      if (apiClient.isSuccessResponse(response)) {
        return {
          success: true,
          message: apiClient.getSuccessMessage(response) || 'Password reset successfully',
          data: response.data
        };
      } else {
        throw new AuthApiError(
          apiClient.getErrorMessage(response),
          apiClient.getErrorCode(response),
          apiClient.getErrorDetails(response)
        );
      }
    } catch (error) {
      console.error('Error confirming password reset:', error);
      
      if (error instanceof AuthApiError) {
        throw error;
      }
      
      if (error instanceof ApiClientError) {
        throw new AuthApiError(
          error.message,
          error.code,
          error.details
        );
      }
      
      throw new AuthApiError(
        'Failed to reset password. Please try again.',
        'NETWORK_ERROR',
        error
      );
    }
  }
}

export const authService = new AuthService();
export default authService; 